import { RequisitionWaiting } from "@/ui/RequisitionWaiting";
import { BRAND_LABEL } from "@/views/Requests";
import { AuditTab } from "@/views/Admin";
import { NotFound } from "@/views/NotFound";
import { STATE_GLYPH, STATE_LABEL } from "@/ui";
import { FilterRow, ActiveFilters } from "@/ui/FilterRow";
import { LEVEL_LABEL, REQUISITION_STATUS_LABEL } from "@/ui/vocabulary";
import { boardColumnOf } from "@/views/requisition/boardColumn";
import { ArchivedRecord } from "@/ui/ArchivedRecord";
import { ReminderDialog } from "@/ui/ReminderDialog";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { candidateList, dispositionOpenWorklist, adminArchiveSearch, requisitionClose, requisitionList, requisitionOpen, requisitionPost, requisitionProposeOpenings, requisitionResume, type RequisitionListInput } from "@/api/tools";
import { useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { CandidatesTab } from "@/views/Candidates";
import { PacketSetPage } from "@/views/PacketSet";
import { requisitionColumns, savedViews } from "@/views/requisition/slots";
import { REQ_V1_ACTIONS } from "@/views/requisition/labels";
import { DebriefsHome } from "@/views/Debriefs";
import { OfferTab } from "@/views/Offer";
import { TIMELINE_LABELS } from "@/views/b10/timeline/TimelineList";
import { SectionNav, Banner, ObjectReadGuard, Dialog, Empty, Field, KV, PhasePill, Pill, Pager, ReasonDialog, Receipt, Skeleton, Table, fmtDate } from "@/ui";
import { Chip, Chips, McRoot } from "@/mc";
import { ARCHIVE_STATUS_CHIPS, activeChip, chipStateParam } from "@/views/b9/archive/chips";
import { ARCHIVE_CHIP_LABELS, ARCHIVE_LABELS } from "@/views/b9/archive/labels";

// V1-INTERACTIONS §4.2 (list · board · Talent Acquisition queue) and §4.3 (the requisition overview and header actions).

const STATUS_TONE: Record<string, "ok" | "warn" | "bad" | "info" | "muted"> = { draft: "muted", open: "ok", on_hold: "warn", partially_filled: "info", accepted: "info", filled: "ok", closed_cancelled: "muted", closed_filled: "muted", archived: "muted" };
const LIVE = ["draft", "open", "on_hold", "partially_filled"];
const ALL_STATUS = Object.entries(REQUISITION_STATUS_LABEL);
const VALUE_LABEL: Record<string, string> = { respect: "Respect", ownership: "Ownership", opportunity: "Opportunity", together: "Together", service: "Service" };
const FORMAT_LABEL: Record<string, string> = { virtual: "Virtual", onsite: "Onsite", virtual_onsite: "Virtual onsite", teams: "Teams", in_person: "In person", phone: "Phone" };
const STAGES = ["Intake", "Sourcing", "Screening", "HM Interview", "Onsite Interviews", "Debrief", "Offer", "Offer Accepted", "Filled", "Closed"];

function useQueryState() {
  const [state, setState] = useState(() => new URLSearchParams(window.location.hash.split("?")[1] ?? ""));
  useEffect(() => {
    const on = () => setState(new URLSearchParams(window.location.hash.split("?")[1] ?? ""));
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  const set = (k: string, v: string | null) => {
    const p = new URLSearchParams(window.location.hash.split("?")[1] ?? "");
    if (k === "clearFilters") { for (const key of ["state", "level", "owner", "department", "brand", "phase", "q"]) p.delete(key); }
    else if (v === null || v === "") p.delete(k);
    else p.set(k, v);
    if (k !== "page" && k !== "view") p.delete("page");
    const base = window.location.hash.split("?")[0] ?? "";
    window.location.hash = p.toString() ? `${base}?${p.toString()}` : base;
  };
  return [state, set] as const;
}

export function RequisitionsList() {
  const { me, can } = useSession();
  const { register } = useViewState();
  const [qs, setQs] = useQueryState();
  const isHm = !!me?.roles.includes("hm") && !me?.roles.includes("recruiter") && !me?.roles.includes("admin");
  const states = qs.get("state")?.split(",").filter(Boolean) ?? [];
  const [search, setSearch] = useState(qs.get("q") ?? "");
  const [reminderFor, setReminderFor] = useState<string | null>(null);
  useEffect(() => setSearch(qs.get("q") ?? ""), [qs]);
  const view = qs.get("view") === "board" ? "board" : "table";
  const input = useMemo<RequisitionListInput>(() => {
    const status = qs.get("state")?.split(",").filter(Boolean);
    const o: RequisitionListInput = { page: Number(qs.get("page") ?? 1) || 1, page_size: 25, sort: (qs.get("sort") as RequisitionListInput["sort"]) ?? "elapsed", owner: qs.get("owner") === "me" ? "mine" : "anyone" };
    if (status && status.length) o.status = status;
    if (qs.get("level")) o.level = qs.get("level");
    if (qs.get("q")) o.q = qs.get("q");
    if (qs.get("department")) o.department = qs.get("department");
    if (qs.get("brand")) o.brand = qs.get("brand");
    if (qs.get("phase")) o.phase_state = qs.get("phase") as "on_track" | "trending_behind" | "behind";
    return o;
  }, [qs]);
  const archived = qs.get("state") === "archived" && can("admin.archive_search");
  const q = useRead(["requisitions", input], () => requisitionList(input), { enabled: !archived });
  useEffect(() => register({ view: "requisitions", ids: {}, summary: `requisitions ${view} · ${q.data?.total ?? "?"} rows` }), [register, view, q.data?.total]);
  const d = q.data;
  const fullRows = d?.items.filter(r => "stage" in r) ?? [];
  const panelistRows = d?.items.filter(r => "projection" in r && r.projection === "panelist") ?? [];
  const openCount = d ? LIVE.reduce((n, s) => n + (d.counts[s] ?? 0), 0) : 0;
  // C-B10-13: the columns and the saved-views control other lanes register (views/requisition/slots.ts)
  const extraColumns = requisitionColumns.list().filter((column) => column.visible?.(can) ?? true);
  const SavedViews = savedViews.list()[0]?.component;
  // B9 S12 (D9, D18): the Status chips; each sends its status list to requisition.list ("Archived records remain
  // searchable/reportable"). Archived rows stay out of the default list; a chip is the way to them. The Archived chip's
  // `state=archived` is also the archive search's page for its holders, which draws the same chips, Archived pressed,
  // so the filter the person chose stays on screen and can be changed from there.
  const statusChips = <McRoot><Chips label={ARCHIVE_LABELS.status}>{ARCHIVE_STATUS_CHIPS.map(chip => <Chip key={chip.key} sm on={activeChip(states) === chip.key} onToggle={on => setQs("state", on ? chipStateParam(chip.key) : null)}>{ARCHIVE_CHIP_LABELS[chip.key]}</Chip>)}</Chips></McRoot>;
  if (archived) return <ArchivedRequisitions statusChips={statusChips} />;
  if (me?.roles.every(role => role === "interviewer")) return <div><h1 className="page">My Requisitions</h1><p className="lede">The requisitions where you hold an interview or backup assignment.</p><Banner error={q.error} />{q.isLoading ? <Skeleton /> : panelistRows.length ? <PanelistRequisitions items={panelistRows} /> : !q.error && <Empty level={2} heading="No requisitions assigned">Your requisitions appear here when an interview assignment is issued.</Empty>}</div>;
  return (
    <div>
      <div className="hrow">
        <div>
          <h1 className="page">{isHm ? "My Requisitions" : "Requisitions"}</h1>
          <p className="lede">{openCount} open · on the checkpoint chain</p>
        </div>
        <div className="acts">
          {SavedViews && <SavedViews query={qs} apply={(next) => { window.location.hash = next ? `#requisitions?${next}` : "#requisitions"; }} />}
          <div className="tabs" role="group" aria-label="View">
            <button aria-pressed={view === "table"} className={view === "table" ? "on" : ""} onClick={() => setQs("view", null)}>
              Table
            </button>
            <button aria-pressed={view === "board"} className={view === "board" ? "on" : ""} onClick={() => setQs("view", "board")}>
              Board
            </button>
          </div>
          {can("request.queue_ta") && (
            <a className="btn btn--primary" href="#requisitions/queue">
              + New requisition
            </a>
          )}
          {isHm && can("request.create") && (
            <a className="btn btn--primary" href="#requests/new">
              + New recruitment request
            </a>
          )}
        </div>
      </div>
      <div className="row filters" role="group" aria-label="Filters">
        <details className="filter-states">
          <summary>State: {states.length ? states.map(state => REQUISITION_STATUS_LABEL[state] ?? state).join(", ") : "Live states"}</summary>
          <fieldset><legend>State</legend>
            {ALL_STATUS.map(([value, label]) => <label key={value} className="check"><input type="checkbox" checked={states.includes(value)} onChange={event => setQs("state", (event.target.checked ? [...states, value] : states.filter(state => state !== value)).join(","))} />{label}</label>)}
          </fieldset>
        </details>
        <label className="filter">
          <span>Level</span>
          <select value={qs.get("level") ?? ""} onChange={(e) => setQs("level", e.target.value)}>
            <option value="">Any</option>
            {Object.entries(LEVEL_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        {!isHm && (
          <label className="filter">
            <span>Owner</span>
            <select value={qs.get("owner") ?? ""} onChange={(e) => setQs("owner", e.target.value)}>
              <option value="">Anyone</option>
              <option value="me">Mine</option>
            </select>
          </label>
        )}
        <label className="filter">
          <span>Phase</span>
          <select value={qs.get("phase") ?? ""} onChange={(e) => setQs("phase", e.target.value)}>
            <option value="">Any</option>
            <option value="on_track">On track</option>
            <option value="trending_behind">Trending behind</option>
            <option value="behind">Behind</option>
          </select>
        </label>
        <label className="filter"><span>Department</span><input value={qs.get("department") ?? ""} onChange={event => setQs("department", event.target.value)} /></label>
        <div className="filter"><label htmlFor="req-brand">Brand</label><select id="req-brand" value={qs.get("brand") ?? ""} onChange={event => setQs("brand", event.target.value)}><option value="">Any</option>{Object.entries(BRAND_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
        <div className="filter"><label htmlFor="req-sort">Sort</label><select id="req-sort" value={qs.get("sort") ?? "elapsed"} onChange={event => setQs("sort", event.target.value)}><option value="elapsed">Elapsed / target</option><option value="activity">Last activity</option><option value="id">REQ</option></select></div>
        <form className="filter-search" role="search" onSubmit={event => { event.preventDefault(); setQs("q", search.trim()); }}>
          <label className="filter filter--grow"><span>Search</span><input type="search" placeholder="R-####, REQ or title" maxLength={120} value={search} onChange={event => setSearch(event.target.value)} /></label>
          <button className="btn btn--sm" type="submit">Search</button>
        </form>
        {[...qs.keys()].some(key => ["state", "level", "owner", "department", "brand", "phase", "q"].includes(key)) && <button className="btn btn--sm" onClick={() => setQs("clearFilters", null)}>Clear filters</button>}
      </div>
      {statusChips}
      <ActiveFilters values={{ ...Object.fromEntries(states.map(state => [`state:${state}`, state])), level: qs.get("level"), owner: qs.get("owner"), department: qs.get("department"), brand: qs.get("brand"), phase: qs.get("phase"), q: qs.get("q") }} labels={{ ...Object.fromEntries(states.map(state => [`state:${state}`, "State"])), q: "Search" }} display={{ ...REQUISITION_STATUS_LABEL, ...LEVEL_LABEL, ...BRAND_LABEL, me: "Mine", on_track: "On track", trending_behind: "Trending behind", behind: "Behind" }} clear={key => key.startsWith("state:") ? setQs("state", states.filter(state => state !== key.slice(6)).join(",")) : setQs(key, null)} />
      <p role="status" className="muted">{d && !q.isFetching ? `${d.total} ${d.total === 1 ? "requisition" : "requisitions"} found` : ""}</p>
      {q.isError && <Banner error={q.error} onRetry={() => void q.refetch()} />}
      {q.isLoading && <Skeleton rows={5} />}
      {d && d.items.length === 0 && <Empty level={2} heading={["state", "level", "owner", "department", "brand", "phase", "q"].some(key => qs.has(key)) ? "No matching requisitions" : "No assigned requisitions yet"} action={["state", "level", "owner", "department", "brand", "phase", "q"].some(key => qs.has(key)) ? <a className="btn" href="#requisitions">Clear filters</a> : can("request.queue_ta") ? <a className="btn" href="#requisitions/queue">Open Talent Acquisition queue</a> : undefined}>{["state", "level", "owner", "department", "brand", "phase", "q"].some(key => qs.has(key)) ? "No requisitions match." : can("request.queue_ta") ? "You do not own a requisition yet. Approved requests waiting for a Recruiter are in the Talent Acquisition queue." : "No requisitions where you are named."}</Empty>}
      {d && d.items.length > 0 && view === "table" && (
        <Table
          label="Requisitions"
          mobile="stack"
          head={
            <tr>
              <th className="sticky-l">REQ</th>
              <th>Role</th>
              <th>Level</th>
              <th>Last activity</th>
              <th>Owner (Recruiter)</th>
              <th>Stage</th>
              <th>Phase target</th>
              <th>Panel</th>
              <th>Waiting on</th>
              {extraColumns.map((column) => <th key={column.key}>{column.header}</th>)}
              <th className="sticky-r cell--actions">Action</th>
            </tr>
          }
        >
          {fullRows.map((r) => (
            <tr key={r.id} className={r.masked ? "masked" : ""}>
              <td className="mono sticky-l nowrap">
                {r.id}
                {r.req_no ? <div className="muted" style={{ whiteSpace: "normal", overflowWrap: "anywhere", maxWidth: "16ch" }}>{r.req_no}</div> : null}
              </td>
              <td>
                {r.masked ? <i>{r.title}</i> : r.title} <Pill tone={STATUS_TONE[r.status] ?? "muted"}>{r.status_label}</Pill>
              </td>
              <td>{r.level_label}</td>
              <td className="muted" title={fmtDate(r.last_activity_at, true)}>{fmtDate(r.last_activity_at)}</td>
              <td>{r.owner_name}</td>
              <td>{r.stage}</td>
              <td>
                <PhasePill phase={r.phase} />
              </td>
              <td className="nowrap">{"panel_needed" in r ? `${r.panel_assigned} / ${r.panel_needed}` : <span className="muted">Restricted</span>}</td>
              <td>{"waiting_on" in r ? <RequisitionWaiting waiting_on={r.waiting_on} waiting_count={r.waiting_count} /> : <span className="muted">Restricted</span>}</td>
              {extraColumns.map((column) => <td key={column.key}>{column.cell(r)}</td>)}
              <td className="sticky-r cell--actions">
                <div style={{ display: "inline-grid", minWidth: "max-content", justifyItems: "start", gap: 4 }}>
                {r.can_open ? (
                  <a className="btn btn--sm btn--primary" aria-label={`Open ${r.id}: ${r.title}`} href={`#requisitions/${r.id}`}>
                    Open <span aria-hidden="true">→</span>
                  </a>
                ) : (
                  <a className="btn btn--sm" href={`#requisitions/${r.id}/access?request=1`} title={`Owned by ${r.owner_name}`}>
                    Request access
                  </a>
                )}
                {can("scorecard.send_reminder") && LIVE.includes(r.status) && (me?.roles.includes("admin") || r.owner_id === me?.person_id) && <button className="btn btn--sm" onClick={() => setReminderFor(r.id)}>Send reminder</button>}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
      {d && d.items.length > 0 && view === "board" && (
        <div className="board" role="region" aria-label="Board" tabIndex={0}>
          {STAGES.map((stage) => {
            const cards = fullRows.filter((r) => boardColumnOf(r.stage, STAGES) === stage);
            return (
              <div key={stage} role="group" aria-labelledby={`board-${STAGES.indexOf(stage)}`} className={`board__col${cards.length ? "" : " board__col--empty"}`}>
                <h2 id={`board-${STAGES.indexOf(stage)}`} className="board__h">
                  {stage} <span className="sec__n">{cards.length}</span>
                </h2>
                {!cards.length && <p className="muted">No requisitions</p>}
                {cards.map((r) => (
                  <a key={r.id} className="board__card" href={r.can_open ? `#requisitions/${r.id}` : `#requisitions/${r.id}/access?request=1`}>
                    <div className="mono">{r.id}</div>
                    <b>{r.title}</b>
                    <div className="muted">
                      {r.level_label} · {r.owner_name}
                    </div>
                    <PhasePill phase={r.phase} />
                  </a>
                ))}
              </div>
            );
          })}
        </div>
      )}
      {panelistRows.length > 0 && <section><h2 className="sec">My interview assignments</h2><PanelistRequisitions items={panelistRows} /></section>}
      {d && d.total > 25 && (
        <div className="row row--between" style={{ marginTop: 10 }}>
          <span className="muted">
            {d.items.length} of {d.total}
          </span>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn btn--sm" disabled={d.page <= 1} onClick={() => setQs("page", String(d.page - 1))}>
              Previous
            </button>
            <button className="btn btn--sm" disabled={d.page * d.page_size >= d.total} onClick={() => setQs("page", String(d.page + 1))}>
              Next
            </button>
          </div>
        </div>
      )}
      {reminderFor && <ReminderDialog requisitionId={reminderFor} onClose={() => setReminderFor(null)} />}
    </div>
  );
}

/**
 * The V1 requisition sections not yet moved under the Mission Control header (candidates, packets, debriefs, offer,
 * activity): V1's header, clocks and section nav over the section, unchanged. Lane B2 S09 moved the bare route (the
 * overview) and the timeline to views/requisition; each owning lane moves its section later (B3, B6, B7, B8, B9).
 */
export function RequisitionPage({ id, tab }: { id: string; tab: string }) {
  const { can } = useSession();
  const { register } = useViewState();
  const q = useRead(["requisition", id], () => requisitionOpen({ requisition_id: id }));
  const worklist = useRead(["worklist", id], () => dispositionOpenWorklist({ requisition_id: id }), { enabled: tab === "offer" });
  const pipeline = useRead(["candidates", id], () => candidateList({ requisition_id: id }), { enabled: tab === "candidates" });
  useEffect(() => register({ view: "requisition", ids: { requisition: id }, summary: `requisition ${id} ${tab} ${q.data && "status" in q.data ? q.data.status : ""}` }), [register, id, tab, q.data]);
  if (tab === "activity" && !can("audit.query")) return <NotFound route={`requisitions/${id}/activity`} />;
  if (!q.data || q.error) return <ObjectReadGuard query={q} />;
  if (tab === "offer" && (!worklist.data || worklist.error)) return <ObjectReadGuard query={worklist} />;
  if (tab === "candidates" && (!pipeline.data || pipeline.error)) return <ObjectReadGuard query={pipeline} />;
  const r = q.data;
  if ("projection" in r && r.projection === "panelist") return <div>
    <h1 className="page">{r.id} · {r.title}</h1><p className="lede">{r.level_label} · {r.department}</p>
    <KV items={[["Reports to", r.reports_to_title], ["Guide version", `v${r.guide_version}`]]} />
    <section><h2 className="sec">My rounds</h2>{r.my_rounds.map(round => <section className="card" key={round.round_def_id}><h3 className="sub">{round.label} · {round.seat === "backup" ? "Backup" : "Interviewer"}</h3><KV items={[["Format", FORMAT_LABEL[round.format] ?? round.format], ["Duration", `${round.duration_minutes} minutes`], ["Values", round.values.map(value => VALUE_LABEL[value] ?? value).join(", ") || "None assigned"], ["Skills", round.skills.map(skill => String(skill.title ?? "")).join(", ") || "None assigned"]]} />{round.packet_id && <a className="btn" href={`#packets/${round.packet_id}`}>Open my packet</a>}</section>)}</section>
    <section className="card"><h2 className="sec">Kickoff email as received</h2>{r.kickoff.length ? r.kickoff.map((mail, i) => <section key={i}><h3 className="sub">{mail.subject}</h3><p className="long">{mail.body ?? "The received email text was not retained."}</p><p className="muted">{fmtDate(mail.sent_at, true)}</p></section>) : <p className="muted">No kickoff email has been retained for you.</p>}</section>
    {r.toolkit_url && <a href={r.toolkit_url}>People Managers Toolkit</a>}
  </div>;
  if (!("checkpoints" in r)) return null;
  const chain = r.checkpoints;
  const current = r.clocks.filter((c) => c.phase_key !== "total" && !c.stopped_at).at(-1);
  const total = r.clocks.find((c) => c.phase_key === "total");
  return (
    <ArchivedRecord record={r}><div>
      <RequisitionV1Controls r={r} withPost head={(
          <div>
            <h1 className="page">
              {r.id} · {r.title}
            </h1>
            <p className="lede">
              {r.req_no ? (
                <>
                  REQ {r.req_no}
                  {!r.req_no_verified && <Pill tone="warn">unverified</Pill>} ·{" "}
                </>
              ) : null}
              {r.level_label} · HM {r.hiring_manager_name} · Recruiter {r.recruiter_name} · HRBP {r.hrbp_name}
            </p>
          </div>
      )} lead={<>
            <Pill tone={STATUS_TONE[r.status] ?? "muted"}>{r.status_label}</Pill>
            {!r.status.startsWith("closed") && String(r.stage).replace(/_/g, " ").toLowerCase() !== String(r.status_label).toLowerCase() && <Pill category tone="muted">{String(r.stage).replace(/_/g, " ")}</Pill>}
      </>} />
      <div className="grid grid--kpi">
        <div className="card kpi">
          <b>
            {r.openings_approved} · {r.offer_commitments} · {r.fills}
          </b>
          <span>openings · offers out · filled</span>
        </div>
        <div className={`card kpi${!current || current.elapsed_days === 0 ? " kpi--zero" : ""}`}>
          <b className={current?.state === "behind" ? "text-bad" : undefined}>{current ? `${current.elapsed_days} / ${current.target_days}` : "No phase running"}</b>
          <span>{current && <><span aria-hidden="true">{STATE_GLYPH[current.state] ?? "○"}</span> {STATE_LABEL[current.state] ?? current.state} · </>}{current ? `${current.label} · day of target` : "Phase clock"}</span>
        </div>
        <div className={`card kpi${!total || total.elapsed_days === 0 ? " kpi--zero" : ""}`}>
          <b className={total?.state === "behind" ? "text-bad" : undefined}>{total ? `${total.elapsed_days} / ${total.target_days}` : "Not started"}</b>
          <span>{total && <><span aria-hidden="true">{STATE_GLYPH[total.state] ?? "○"}</span> {STATE_LABEL[total.state] ?? total.state} · </>}Time to Acceptance · Active Time · days{total ? ` · Calendar Time ${total.calendar_days} · Days on Hold ${total.held_days}` : ""}</span>
        </div>
        <div className="card kpi">
          <b>v{r.guide_version}</b>
          <span>guide version pinned</span>
        </div>
      </div>
      <ol className="chain" aria-label="Checkpoint chain" tabIndex={0}>
        {chain.map((c) => (
          <li key={c.name} tabIndex={c.occurred_at ? 0 : undefined} aria-current={c.state === "current" ? "step" : undefined} className={`chain__step chain__step--${c.state}`} title={c.occurred_at ? `${c.label} · ${fmtDate(c.occurred_at, true)}` : c.label}>
            <span className="chain__dot" aria-hidden="true">{c.state === "done" ? "✓" : ""}</span><span className="sr-only">{c.state === "done" ? "Completed" : c.state === "current" ? "Current" : "Not started"}: </span>
            <span className="chain__label">{c.label}</span>
            {c.occurred_at && <span className="chain__date">{fmtDate(c.occurred_at)}</span>}
          </li>
        ))}
      </ol>
      <SectionNav label="Requisition sections" links={[
          ["", "Overview"],
          ["intake", "Intake & Panel"],
          ["candidates", "Candidates"],
          ["packets", "Packets"],
          ["debriefs", "Summary & Debriefs"],
          ["offer", "Offer & close"],
          // §4.23: the Code of Conduct screen is reached from the requisition it belongs to, not only from the
          // notification that told somebody about it — the screen itself decides what each reader is shown (Codex #9 M10)
          ["concerns", "Conduct"],
          ["access", "Access"],
          ["timeline", TIMELINE_LABELS.tab],
          ["activity", "Activity"],
        ].filter(([key]) => key !== "activity" || can("audit.query")).map(([key, label]) => ({ label: label!, href: `#requisitions/${r.id}${key ? "/" + key : ""}`, current: (tab || "") === key }))} />
      {tab === "activity" && can("audit.query") && <AuditTab key={id} requisitionId={id} />}
      {tab === "candidates" && <CandidatesTab id={r.id} allowAdd={r.can["add_candidate"] ?? false} />}
      {tab === "packets" && <PacketSetPage id={r.id} />}
      {tab === "debriefs" && <DebriefsHome rid={r.id} />}
      {tab === "offer" && <OfferTab rid={r.id} />}

    </div></ArchivedRecord>
  );
}

type V1Requisition = Extract<Awaited<ReturnType<typeof requisitionOpen>>, { checkpoints: unknown }>;

/**
 * The V1 header actions with no Mission Control word yet, and their dialogs: record after the fact (the bare route
 * passes it as `head`), Change openings / Review openings changes, Request access, Resume (direct, no word of his yet)
 * and the filled close (lane B9's). Hold, cancel and reopen are asked for and decided on the Mission Control
 * requisition page (lane B2 S10, views/requisition/StateChange.tsx), never here.
 * The bare route renders it as a V1 sibling outside `.mc` (`withPost` false: the Coordinator's Post is on the Mission
 * Control header); the V1 sections render it in their header with Post (V1 behaviour, unchanged).
 */
export function RequisitionV1Controls({ r, withPost = false, head, lead, openings: menuOpenings }: {
  r: V1Requisition; withPost?: boolean; head?: ReactNode; lead?: ReactNode;
  /** F-186: the openings change is an item of the Mission Control header's Actions menu (it opens this dialog from the
   * URL), so the row draws neither Change openings nor Review openings changes, and draws itself only when another of
   * its controls is shown. */
  openings?: { open: boolean; onClose: () => void } | undefined;
}) {
  const inval = [["requisition", r.id], ["requisitions"], ["today"], ["intake", r.id]] as const;
  const post = useWrite((i: { requisition_id: string; req_no: string; posting_channels: ("internal" | "external")[]; posting_url?: string | null }, k) => requisitionPost(i, k), inval);
  const resume = useWrite((i: { requisition_id: string; note?: string | null }, k) => requisitionResume(i, k), inval);
  // B2 S07: `close` closes a filled search; a cancellation is requested and the HR Business Partner approves it
  const close = useWrite((i: { requisition_id: string; note?: string | null }, k) => requisitionClose({ ...i, reason: "filled" }, k), inval);
  const openings = useWrite((i: { requisition_id: string; to_count: number; reason: string }, k) => requisitionProposeOpenings(i, k), inval);
  const [dlg, setDlg] = useState<"post" | "resume" | "close" | "openings" | null>(null);
  const [toCount, setToCount] = useState(1);
  const [reqNo, setReqNo] = useState("");
  const [channels, setChannels] = useState<("internal" | "external")[]>(["internal", "external"]);
  const [url, setUrl] = useState("");
  // opened from the menu, the dialog starts at the approved count, as the button set it
  const menuOpen = menuOpenings?.open ?? false;
  useEffect(() => { if (menuOpen) setToCount(r.openings_approved); }, [menuOpen, r.openings_approved]);
  const openingsOpen = menuOpenings ? menuOpenings.open : dlg === "openings";
  const closeOpenings = () => (menuOpenings ? menuOpenings.onClose() : setDlg(null));
  const row = !menuOpenings || head !== undefined || lead !== undefined || withPost || r.can.resume || r.can.close || r.can.request_access;
  return (
    <>
      {row && <div className="hrow">
        {head ?? <span />}
        <div className="acts">
          {lead}
          {withPost && r.can.post && (
            <button className="btn btn--primary" onClick={() => setDlg("post")}>
              Post the position
            </button>
          )}
          {withPost && !r.can.post && r.status === "draft" && (
            <a className="btn" href={`#requisitions/${r.id}/intake/part0`} title="Complete Part 0 with the job description first">
              Complete Part 0 to post
            </a>
          )}
          {r.can.resume && (
            <button className="btn btn--primary" onClick={() => setDlg("resume")}>
              Resume
            </button>
          )}
          {r.can.close && (
            <button className="btn" onClick={() => setDlg("close")}>
              Close
            </button>
          )}
          {/* one of the header's actions, drawn like the others: as a ghost it lost its border and read as a stray line of
              text under the status pills instead of a control on the action row (UAT 02 finding 8) */}
          {!menuOpenings && r.can.propose_openings && (
            <button className="btn" onClick={() => { setToCount(r.openings_approved); setDlg("openings"); }}>
              {REQ_V1_ACTIONS.changeOpenings}
            </button>
          )}
          {!menuOpenings && r.can.change_openings && <a className="btn" href="#proposals">{REQ_V1_ACTIONS.reviewOpeningsChanges}</a>}
          {r.can.request_access && (
            <a className="btn" href={`#requisitions/${r.id}/access?request=1`}>
              Request access
            </a>
          )}
        </div>
      </div>}
      <Dialog title="Post the position" open={dlg === "post"} onClose={() => setDlg(null)}>
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void post.mutateAsync({ requisition_id: r.id, req_no: reqNo.trim(), posting_channels: r.confidential ? [] : channels, posting_url: url || null }).then(() => setDlg(null));
          }}
        >
          <Field label="REQ #" required id="p-req" hint="The requisition number from the ATS; an unverified number shows a chip until the ATS confirms it" error={post.error?.fieldErrors.req_no}>
            <input id="p-req" value={reqNo} onChange={(e) => setReqNo(e.target.value)} required />
          </Field>
          {!r.confidential && (
            <fieldset>
              <legend>Posting channels</legend>
              <div className="checks">
                {(["internal", "external"] as const).map((c) => (
                  <label key={c} className="check">
                    <input type="checkbox" checked={channels.includes(c)} onChange={(e) => setChannels(e.target.checked ? [...channels, c] : channels.filter((x) => x !== c))} /> {c === "internal" ? "Internal" : "External"}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          {r.confidential && <p className="muted">Confidential search: no posting channel; the position is not advertised.</p>}
          <Field label="Posting URL (optional)" id="p-url">
            <input id="p-url" value={url} onChange={(e) => setUrl(e.target.value)} />
          </Field>
          <Banner error={post.error} />
          <div className="row row--end" style={{ gap: 8 }}>
            <button type="button" className="btn" onClick={() => setDlg(null)}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={post.isPending || !reqNo.trim()}>
              {post.isPending ? "Posting…" : "Post"}
            </button>
            {!reqNo.trim() && <span className="muted">Enter the REQ # to post.</span>}
          </div>
        </form>
      </Dialog>
      {post.isSuccess && <Receipt>Posted · the Posting checkpoint is stamped and the Time to Acceptance clock has started.</Receipt>}
      {withPost && r.status === "on_hold" && (
        <div className="callout callout--warn" role="status">
          On hold — every clock is paused and pausable timers are suspended; the owner resumes.
        </div>
      )}
      <ReasonDialog title="Resume" label="Note (optional context)" confirm="Resume" open={dlg === "resume"} onClose={() => setDlg(null)} busy={resume.isPending} error={resume.error} onConfirm={(note) => void resume.mutateAsync({ requisition_id: r.id, note }).then(() => setDlg(null))} />
      <ReasonDialog title="Close the requisition" label="Note" confirm="Close" open={dlg === "close"} onClose={() => setDlg(null)} busy={close.isPending} error={close.error} onConfirm={(note) => void close.mutateAsync({ requisition_id: r.id, note }).then(() => setDlg(null))}>
        <p className="muted">Closed — filled (from Filled, disposition worklist empty). The HR Business Partner and the Hiring Manager receive a notice.</p>
      </ReasonDialog>
      <ReasonDialog title={REQ_V1_ACTIONS.changeOpenings} confirm="Propose" open={!!r.can.propose_openings && openingsOpen} onClose={closeOpenings} busy={openings.isPending} error={openings.error} onConfirm={(reason) => void openings.mutateAsync({ requisition_id: r.id, to_count: toCount, reason }).then(closeOpenings)}>
        <Field label="New number of openings" required id="o-count" hint={`Currently ${r.openings_approved}; never below ${r.fills + r.offer_commitments} (filled + offers out)`}>
          <input id="o-count" type="number" min={1} max={50} value={toCount} onChange={(e) => setToCount(Number(e.target.value) || 1)} />
        </Field>
        <p className="muted">{r.hrbp_name} approves; the header count changes on approval.</p>
      </ReasonDialog>
      {(resume.isSuccess || close.isSuccess) && <Receipt>Recorded.</Receipt>}
      {openings.isSuccess && <Receipt>Openings change proposed — waiting on {r.hrbp_name}.</Receipt>}
    </>
  );
}

// Archive has a retained-seat search of its own; the live list's status is the close outcome.
function ArchivedRequisitions({ statusChips }: { statusChips: ReactNode }) {
  const [query, setQuery] = useState("");
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const q = useRead(["archive-list", query, pages.at(-1)], () => adminArchiveSearch({ query: query || null, limit: 25, cursor: pages.at(-1) ?? null }));
  return <section><h1 className="page">Requisitions</h1><p className="lede">Archived evidence · read-only in the seat held at archive time. <a href="#requisitions">Live requisitions</a></p>
    {statusChips}
    <FilterRow><Field label="Search archived requisitions" id="archive-requisitions-query"><div className="search-control"><span aria-hidden="true">⌕</span><input id="archive-requisitions-query" placeholder="Requisition number or title" value={query} onChange={e => { setQuery(e.target.value); setPages([null]); }} /></div></Field></FilterRow>
    <ActiveFilters values={{ query }} labels={{ query: "Search" }} clear={() => { setQuery(""); setPages([null]); }} />
    {q.data && <p role="status" className="muted">{q.data.items.length} archived requisitions on this page</p>}
    <Banner error={q.error} onRetry={() => void q.refetch()} />{q.isLoading && <Skeleton />}
    {q.data && (q.data.items.length ? <Table label="Archived requisitions" head={<tr><th>Requisition</th><th>Archived</th><th>Reason</th></tr>}>{q.data.items.map(r => <tr key={String(r.id)}><td><a href={`#requisitions/${r.id}`}>{String(r.id)}</a><div>{String(r.title ?? "")}</div></td><td>{fmtDate(r.archived_at as string, true)}</td><td className="long">{String(r.archive_reason ?? "")}</td></tr>)}</Table> : <Empty level={2} heading={query ? "No archived requisitions match that search" : "No archived requisitions yet"} action={query ? <button className="btn" onClick={() => { setQuery(""); setPages([null]); }}>Clear search</button> : undefined}>{query ? "Try a requisition number or title." : "Archived evidence appears here when a requisition is archived."}</Empty>)}
    <Pager count={q.data?.items.length ?? 0} page={pages.length} previous={pages.length > 1 ? () => setPages(pages.slice(0, -1)) : undefined} next={q.data?.next_cursor ? () => setPages([...pages, q.data!.next_cursor!]) : undefined} />
  </section>;
}


function PanelistRequisitions({ items }: { items: { id: string; req_no: string | null; title: string; level_label: string; department: string }[] }) {
  return <Table label="My assigned requisitions" caption="Requisitions with your interview assignments" head={<tr><th scope="col">Requisition</th><th scope="col">Role</th><th scope="col">Level</th><th scope="col">Department</th><th scope="col">Action</th></tr>}>
    {items.map(r => <tr key={r.id}><th scope="row">{r.req_no ?? r.id}</th><td>{r.title}</td><td>{r.level_label}</td><td>{r.department}</td><td><a className="btn btn--sm" href={`#requisitions/${r.id}`}>Open {r.id} →</a></td></tr>)}
  </Table>;
}
